import React, { useMemo, useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import { useDeleteLabRow, useLabDeviceTypes } from '../../hooks/lab-hooks';
import { LabDeviceType, labTransportLabel, LAB_TABLES } from '../../support/labService';
import LabDeviceTypeEditorModal from './LabDeviceTypeEditorModal';
import { LabCard, LabPanelState, LabStatusTag, labErrorMessage } from './LabShared';

export type LabDeviceTypesPanelProps = {
  /** Authoring. The API refuses either way; this decides whether the buttons are there. */
  canEdit?: boolean;
  selectedId?: number | null;
  onSelect?: (deviceType: LabDeviceType) => void;
};

/**
 * What kinds of box a lab is built out of.
 *
 * Cisco products, Windows, the Linux variants, vCenter - and whatever the seventh one turns out to
 * be, which is why this is a table. A type is what lets an item say "this is written for a firewall"
 * and have that mean something when somebody attaches it to a Windows host.
 *
 * The method column is the load-bearing one. Empty means unconstrained rather than nothing-allowed:
 * a half-filled list refuses real work for a reason nobody chose, so the seed only fills it in where
 * it is sure.
 */
const LabDeviceTypesPanel: React.FC<LabDeviceTypesPanelProps> = ({
  canEdit = false,
  selectedId,
  onSelect,
}) => {
  const [editor, setEditor] = useState<{ open: boolean; deviceType: LabDeviceType | null }>({
    open: false,
    deviceType: null,
  });
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { data: catalogue, isLoading, error } = useLabDeviceTypes();
  const deleteType = useDeleteLabRow(LAB_TABLES.deviceType);

  const rows = useMemo(() => catalogue || [], [catalogue]);

  const remove = async (deviceType: LabDeviceType) => {
    const items = deviceType.item_count ?? 0;
    const devices = deviceType.device_count ?? 0;
    const used = items + devices;

    // eslint-disable-next-line no-restricted-globals
    if (
      !window.confirm(
        used > 0
          ? `${deviceType.name} is named by ${items} ${
              items === 1 ? 'item' : 'items'
            } and ${devices} ${
              devices === 1 ? 'device' : 'devices'
            }. Deleting it will be refused while that is true - they hold it in place. Disable it instead to keep them working and take it out of the pick lists. Try anyway?`
          : `Delete ${deviceType.name}? Nothing refers to it.`,
      )
    ) {
      return;
    }

    setRemoveError(null);

    try {
      await deleteType.mutateAsync(deviceType.id);
    } catch (exc) {
      setRemoveError(
        labErrorMessage(
          exc,
          'Could not delete the device type. Items and devices that name it hold it in place - disable it instead.',
        ),
      );
    }
  };

  return (
    <>
      <LabCard
        title={`Device types (${rows.length})`}
        subtitle="What kind of box an item is written for, and what a scenario device claims to be. A table rather than a list, because the next platform arrives without a release."
        actions={
          canEdit ? (
            <StbButton
              role="add"
              size="small"
              onClick={async () => setEditor({ open: true, deviceType: null })}
            >
              New device type
            </StbButton>
          ) : undefined
        }
      >
        {removeError && <p className="m-0 mb-3 text-sm text-amber-800">{removeError}</p>}

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!rows.length}
          emptyMessage="No device types yet, so an item cannot say what it is written for and nothing is checked when one is attached to a device. Run seed_lab_reference_data for the usual six, or author them here."
          loadingLabel="Loading device types..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Type</th>
                  <th className="px-3 py-2 font-semibold">Product</th>
                  <th className="px-3 py-2 font-semibold">Reached by</th>
                  <th className="px-3 py-2 font-semibold">Allowed methods</th>
                  <th className="px-3 py-2 font-semibold">Used by</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((deviceType) => {
                  const selected = selectedId === deviceType.id;
                  const methodKeys = deviceType.method_keys || [];
                  const items = deviceType.item_count ?? 0;
                  const devices = deviceType.device_count ?? 0;

                  return (
                    <tr
                      key={deviceType.id}
                      className={`border-b border-gray-100 align-top ${selected ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium">{deviceType.name}</div>
                        <div className="font-mono text-xs text-gray-500">{deviceType.key}</div>
                        {deviceType.description && (
                          <div className="mt-1 text-xs text-gray-500">{deviceType.description}</div>
                        )}

                        <div className="mt-1 flex flex-wrap gap-1">
                          {!deviceType.enabled && (
                            <LabStatusTag label="Disabled" sentiment="warning" />
                          )}
                          {selected && <LabStatusTag label="Selected" sentiment="info" />}
                        </div>
                      </td>

                      <td className="px-3 py-2">
                        {deviceType.product_uid ? (
                          <>
                            <div>{deviceType.product_name || `product ${deviceType.product_uid}`}</div>
                            {/* The id is what is stored; the name beside it is a snapshot. */}
                            <div className="font-mono text-xs text-gray-500">
                              {deviceType.product_uid}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs text-gray-500">Not a Cisco product</span>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div>{labTransportLabel(deviceType.default_transport)}</div>
                        <div className="text-xs text-gray-500">
                          {deviceType.default_port
                            ? `port ${deviceType.default_port}`
                            : "the transport's own port"}
                        </div>
                      </td>

                      <td className="px-3 py-2">
                        {methodKeys.length ? (
                          <div className="flex flex-wrap gap-1">
                            {methodKeys.map((key) => (
                              <LabStatusTag key={key} label={key} />
                            ))}
                          </div>
                        ) : (
                          <>
                            <LabStatusTag label="Unconstrained" sentiment="warning" />
                            <div className="mt-1 text-xs text-gray-500">
                              Any method may be used against it - nothing is checked.
                            </div>
                          </>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div>
                          {items} {items === 1 ? 'item' : 'items'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {devices} {devices === 1 ? 'device' : 'devices'}
                        </div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {onSelect && !selected && (
                            <StbButton
                              role="default"
                              size="xsmall"
                              onClick={async () => onSelect(deviceType)}
                            >
                              Select
                            </StbButton>
                          )}
                          {canEdit && (
                            <>
                              <StbButton
                                role="update"
                                size="xsmall"
                                onClick={async () => setEditor({ open: true, deviceType })}
                              >
                                Edit
                              </StbButton>
                              <StbButton
                                role="delete"
                                size="xsmall"
                                loading={deleteType.isPending}
                                onClick={async () => remove(deviceType)}
                              >
                                Delete
                              </StbButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <LabDeviceTypeEditorModal
        open={editor.open}
        deviceType={editor.deviceType}
        onClose={() => setEditor({ open: false, deviceType: null })}
        onSaved={(saved) => onSelect?.(saved)}
      />
    </>
  );
};

export default LabDeviceTypesPanel;
